import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCookies } from "react-cookie";
import SymbolSearchPopup from "./SymbolSearchPopup";

const formatSymbolLabel = (symbol) => {
  if (!symbol) return "";
  const slashIndex = symbol.indexOf("/");
  return slashIndex > 0 ? symbol.substring(0, slashIndex) : symbol;
};

export default function AdminBandBulkOps() {
  const [cookies] = useCookies(["uuid"]);
  const [authChecked, setAuthChecked] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUids, setSelectedUids] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [logs, setLogs] = useState([]);
  const [clearScope, setClearScope] = useState("all");
  const [selectedSymbol, setSelectedSymbol] = useState("");
  const [isSymbolPopupOpen, setIsSymbolPopupOpen] = useState(false);
  const [symbolPopupAnchor, setSymbolPopupAnchor] = useState(null);

  const appendLog = useCallback((message) => {
    setLogs((prev) => [
      { id: `${Date.now()}-${prev.length}`, message, at: new Date().toLocaleTimeString() },
      ...prev,
    ].slice(0, 200));
  }, []);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("/admin/band/bulk_clear/candidates", {
        credentials: "include",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        throw new Error(data.message || "후보 목록을 불러오지 못했습니다.");
      }
      setCandidates(data.candidates || []);
      setSelectedUids(new Set());
    } catch (error) {
      alert(error.message || error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!cookies.uuid) {
      alert("로그인 정보가 없습니다. 관리자 로그인 페이지로 이동합니다.");
      window.location.href = "/AdminLogin";
      return;
    }

    fetch(`/SetPage/admin_user?uuid=${cookies.uuid}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("서버 응답 실패");
        }
        return response.json();
      })
      .then((data) => {
        const user = data[0];
        if (!user) {
          throw new Error("관리자 정보 없음");
        }
        const readOnly = user.admin_possible === "1" || user.admin_possible === 1;
        if (readOnly) {
          alert("읽기 전용 관리자는 이 페이지를 사용할 수 없습니다.");
          window.location.href = "/Adminpage";
          return;
        }
        setAuthChecked(true);
        fetchCandidates();
      })
      .catch(() => {
        alert("관리자 인증에 실패했습니다.");
        window.location.href = "/AdminLogin";
      });
  }, [cookies.uuid, fetchCandidates]);

  const symbolFilterActive = clearScope === "symbol" && selectedSymbol;

  const filteredCandidates = useMemo(() => {
    let rows = candidates;
    if (symbolFilterActive) {
      rows = rows.filter((row) => (row.symbols || []).includes(selectedSymbol));
    }
    const query = searchQuery.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter(
      (row) =>
        String(row.uid || "").toLowerCase().includes(query) ||
        String(row.uid_2 || "").toLowerCase().includes(query) ||
        String(row.nickname || "").toLowerCase().includes(query)
    );
  }, [candidates, searchQuery, symbolFilterActive, selectedSymbol]);

  const handleOpenSymbolPopup = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const popupWidth = 300;
    const popupHeight = 320;
    const top = Math.max(16, rect.top - popupHeight - 12);
    const left = Math.min(
      Math.max(16, rect.left),
      viewportWidth - popupWidth - 16
    );
    setSymbolPopupAnchor({ top, left });
    setIsSymbolPopupOpen(true);
  };

  const handleCloseSymbolPopup = () => {
    setIsSymbolPopupOpen(false);
    setSymbolPopupAnchor(null);
  };

  const allFilteredSelected =
    filteredCandidates.length > 0 &&
    filteredCandidates.every((row) => selectedUids.has(row.uid));

  const toggleSelectAllFiltered = () => {
    setSelectedUids((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredCandidates.forEach((row) => next.delete(row.uid));
      } else {
        filteredCandidates.forEach((row) => next.add(row.uid));
      }
      return next;
    });
  };

  const toggleUid = (uid) => {
    setSelectedUids((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  };

  const handleBulkClear = async () => {
    const uids = Array.from(selectedUids);
    if (uids.length === 0) {
      alert("정리할 회원을 선택해 주세요.");
      return;
    }

    if (clearScope === "symbol" && !selectedSymbol) {
      alert("정리할 심볼을 선택해 주세요.");
      return;
    }

    const symbolLine = selectedSymbol
      ? `\n대상 심볼: ${formatSymbolLabel(selectedSymbol)} (${selectedSymbol})`
      : "\n대상: 각 회원의 활성 코인 전체";

    const confirmed = window.confirm(
      `선택한 ${uids.length}명의 Band 코인을 일괄 정리합니다.${symbolLine}\n` +
        "포지션 청산·주문 취소가 실행되며 시간이 걸릴 수 있습니다.\n" +
        "※ 더블 모드 활성 코인은 메인·보조 계정이 함께 정리됩니다.\n\n계속하시겠습니까?"
    );
    if (!confirmed) return;

    setRunning(true);
    setProgress({ done: 0, total: uids.length, currentUid: "" });
    appendLog(
      `일괄 정리 시작 (${uids.length}명${selectedSymbol ? ` · ${selectedSymbol}` : ""})`
    );

    let successCount = 0;
    let failCount = 0;

    for (let index = 0; index < uids.length; index += 1) {
      const uid = uids[index];
      setProgress({ done: index, total: uids.length, currentUid: uid });

      try {
        const response = await fetch("/admin/band/bulk_clear", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            uid,
            ...(clearScope === "symbol" && selectedSymbol ? { symbol: selectedSymbol } : {}),
          }),
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || !data.ok) {
          throw new Error(data.message || "정리 실패");
        }

        if (data.total === 0) {
          appendLog(
            `[${uid}] ${selectedSymbol ? `${formatSymbolLabel(selectedSymbol)} 활성 없음` : "활성 코인 없음"}`
          );
        } else if (data.failed?.length) {
          appendLog(
            `[${uid}] ${data.cleared.length}/${data.total} 완료, 실패 ${data.failed.length}개`
          );
        } else {
          appendLog(`[${uid}] ${data.cleared.length}개 코인 정리 완료`);
        }
        successCount += 1;
      } catch (error) {
        failCount += 1;
        appendLog(`[${uid}] 오류: ${error.message || error}`);
      }

      setProgress({ done: index + 1, total: uids.length, currentUid: uid });
    }

    setRunning(false);
    setProgress(null);
    appendLog(`완료 — 성공 ${successCount}명, 실패 ${failCount}명`);
    alert(`일괄 정리가 끝났습니다.\n성공: ${successCount}명\n실패: ${failCount}명`);
    await fetchCandidates();
  };

  const handleLogout = () => {
    const uuid = cookies.uuid;
    if (!uuid) {
      window.location.href = "/AdminLogin";
      return;
    }
    fetch("/super_logout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: uuid }),
    }).finally(() => {
      window.location.href = "/AdminLogin";
    });
  };

  if (!authChecked) {
    return (
      <main className="admin-dashboard">
        <section className="admin-dashboard__panel">
          <p className="admin-dashboard__eyebrow">인증 확인 중...</p>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-dashboard admin-wz-bulk">
      <div className="admin-dashboard__glare" aria-hidden="true" />
      <section className="admin-dashboard__panel">
        <header className="admin-dashboard__hero">
          <div>
            <p className="admin-dashboard__eyebrow">Band admin · Bulk ops</p>
            <h1 className="admin-dashboard__title">Band 코인 일괄 정리</h1>
            <p className="admin-dashboard__subtitle" style={{ marginTop: 8, opacity: 0.85 }}>
              진입중·익절후 대기·재시작 상태 코인을 SetpageBand와 동일하게 정리합니다.
              <br />
              더블 모드 코인은 메인·보조 계정이 동시에 정리됩니다 (포지션 청산·주문 취소·DB 차단).
            </p>
          </div>
          <div className="admin-dashboard__hero-actions">
            <Link to="/AdminBand" className="admin-dashboard__btn admin-dashboard__btn--ghost">
              Band 관리
            </Link>
            <button
              type="button"
              className="admin-dashboard__btn admin-dashboard__btn--danger"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </header>

        <div className="admin-wz-bulk__stack">
          <article className="admin-wz-bulk__card admin-wz-bulk__card--wide">
            <header className="admin-wz-bulk__card-head">
              <span className="admin-wz-bulk__step">1</span>
              <div>
                <h2 className="admin-wz-bulk__card-title">정리 범위</h2>
                <p className="admin-wz-bulk__card-desc">
                  전체 활성 코인 또는 Bitget 심볼 하나만 선택해 정리할 수 있습니다
                </p>
              </div>
            </header>

            <div className="admin-wz-bulk-ops__scope">
              <label className="admin-wz-bulk-ops__scope-option">
                <input
                  type="radio"
                  name="bandClearScope"
                  value="all"
                  checked={clearScope === "all"}
                  onChange={() => setClearScope("all")}
                  disabled={running}
                />
                활성 코인 전체 정리
              </label>
              <label className="admin-wz-bulk-ops__scope-option">
                <input
                  type="radio"
                  name="bandClearScope"
                  value="symbol"
                  checked={clearScope === "symbol"}
                  onChange={() => setClearScope("symbol")}
                  disabled={running}
                />
                선택 심볼만 정리
              </label>
            </div>

            {clearScope === "symbol" ? (
              <div
                className={`admin-wz-bulk__symbol-pick${
                  selectedSymbol ? " is-selected" : ""
                }`}
              >
                <span className="admin-wz-bulk__symbol-value">
                  {selectedSymbol
                    ? `${formatSymbolLabel(selectedSymbol)} · ${selectedSymbol}`
                    : "심볼 미선택"}
                </span>
                <button
                  type="button"
                  className="admin-dashboard__btn admin-dashboard__btn--ghost admin-wz-bulk__symbol-btn"
                  onClick={handleOpenSymbolPopup}
                  disabled={running}
                >
                  심볼 검색/선택
                </button>
              </div>
            ) : null}
          </article>

          <SymbolSearchPopup
            isOpen={isSymbolPopupOpen}
            anchorPosition={symbolPopupAnchor}
            onAdd={(sym) => {
              setSelectedSymbol(sym);
              handleCloseSymbolPopup();
            }}
            onClose={handleCloseSymbolPopup}
          />

          <article className="admin-wz-bulk__card admin-wz-bulk__card--wide">
            <header className="admin-wz-bulk__card-head">
              <span className="admin-wz-bulk__step">2</span>
              <div>
                <h2 className="admin-wz-bulk__card-title">정리 대상 회원</h2>
                <p className="admin-wz-bulk__card-desc">
                  {symbolFilterActive
                    ? `선택 심볼을 보유·구동 중인 회원만 표시 (${formatSymbolLabel(selectedSymbol)})`
                    : "활성 Band 코인이 있는 회원만 표시됩니다. 보조 UID가 등록된 회원은 함께 표시됩니다."}
                </p>
              </div>
            </header>

            <div className="admin-wz-bulk__toolbar">
              <div className="admin-wz-bulk__search">
                <input
                  type="text"
                  className="admin-wz-bulk__search-input"
                  placeholder="UID(메인·보조) 또는 닉네임 검색"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={running}
                />
                <span className="admin-wz-bulk__search-meta">
                  {filteredCandidates.length}명 · 선택 {selectedUids.size}명
                </span>
              </div>
              <div className="admin-wz-bulk__action-groups">
                <div className="admin-wz-bulk__action-group">
                  <button
                    type="button"
                    className="admin-dashboard__btn admin-dashboard__btn--ghost admin-wz-bulk__refresh-btn"
                    onClick={fetchCandidates}
                    disabled={loading || running}
                  >
                    {loading ? "불러오는 중..." : "목록 새로고침"}
                  </button>
                  <button
                    type="button"
                    className="admin-dashboard__btn admin-dashboard__btn--approve"
                    onClick={handleBulkClear}
                    disabled={running || selectedUids.size === 0}
                  >
                    {running
                      ? "정리 진행 중..."
                      : clearScope === "symbol"
                        ? "선택 회원 · 심볼 정리"
                        : "선택 회원 일괄 정리"}
                  </button>
                </div>
              </div>
            </div>

            {progress ? (
              <div className="admin-wz-bulk__progress" role="status">
                <strong>
                  진행 {progress.done} / {progress.total}
                </strong>
                {progress.currentUid ? (
                  <span> — 현재 UID: {progress.currentUid}</span>
                ) : null}
              </div>
            ) : null}

            <div className="admin-dashboard__table-wrapper admin-wz-bulk__table-wrap">
              <table className="admin-dashboard__table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={toggleSelectAllFiltered}
                        disabled={running || filteredCandidates.length === 0}
                        aria-label="현재 목록 전체 선택"
                      />
                    </th>
                    <th>메인 UID</th>
                    <th>보조 UID</th>
                    <th>닉네임</th>
                    <th>활성 코인 수</th>
                    <th>코인 목록</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCandidates.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        <div className="admin-dashboard__empty">
                          {loading ? "불러오는 중..." : "정리 대상 회원이 없습니다."}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredCandidates.map((row) => (
                      <tr key={row.uid}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedUids.has(row.uid)}
                            onChange={() => toggleUid(row.uid)}
                            disabled={running}
                          />
                        </td>
                        <td>{row.uid}</td>
                        <td>
                          {row.uid_2 ? (
                            <span title={row.has_double_active ? "더블 모드 활성 코인 있음 — 정리 시 보조 계정도 함께 처리됩니다" : "보조 계정 등록됨"}>
                              {row.uid_2}
                              {row.has_double_active ? (
                                <span className="admin-hold-bulk-ops__double-tag">더블</span>
                              ) : null}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>{row.nickname}</td>
                        <td>{row.active_coin_count}</td>
                        <td className="admin-wz-bulk__symbols">
                          {(row.symbols || []).map((symbol) => formatSymbolLabel(symbol)).join(", ")}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </article>
        </div>

        {logs.length > 0 ? (
          <div className="admin-wz-bulk__log">
            <h2 className="admin-wz-bulk__log-title">작업 로그</h2>
            <ul>
              {logs.map((entry) => (
                <li key={entry.id}>
                  <span>{entry.at}</span> {entry.message}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>
    </main>
  );
}
